import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../utils/apiConfig';

const TIMEFRAMES = ['1h', '4h', 'daily'];
const POLL_INTERVAL = 30_000;

export function useBtcPredictions({ enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('1h');
  const timeframeRef = useRef(timeframe);

  // Keep ref in sync for use inside fetchData
  useEffect(() => {
    timeframeRef.current = timeframe;
  }, [timeframe]);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/btc-predictions`);

      if (!response.ok) {
        throw new Error(`Failed to fetch predictions: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch predictions');
      }

      const newData = result.data;
      if (newData != null) {
        setData(newData);
      }
      setError(null);

      // Auto-switch if current timeframe is null
      if (newData && newData[timeframeRef.current] == null) {
        const available = TIMEFRAMES.find((tf) => newData[tf] != null);
        if (available) {
          setTimeframe(available);
        }
      }
    } catch (err) {
      console.error('[BtcPredictions] Fetch error:', err);
      setError(err.message);
      // Don't clear previous data on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    fetchData();

    const interval = setInterval(fetchData, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchData, enabled]);

  return {
    data,
    loading,
    error,
    timeframe,
    setTimeframe,
    refresh: fetchData,
  };
}
